import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { typography } from "../../../theme/typography";
import { common, danger, neutral } from "../../../theme/colors";
import { useThemeContext } from "../../../theme/ThemeContextProvider";
import { ReactComponent as PasteIcon } from "../../../assets/paste.svg";
import { ReactComponent as ErrorIcon } from "../../../assets/error.svg";
import useAuthenticatedFetch from "../../../auth/authenticated";
import { FEEDBACK } from "../../../models/Feedback";
import { AUTO_HIDE_DURATION_ERROR } from "../../../Common";
import { ReactComponent as CheckCircleIcon } from "../../../assets/check-circle.svg";
import { ReactComponent as CloseIconWhite } from "../../../assets/close-white.svg";
import { modalBoxStyle, modalCardStyle } from "../../../theme/theme";

interface MergeModalProps {
  open: boolean;
  onClose: (event?: any) => void;
  sourceClaimId: string;
}

const MergeModal: React.FC<MergeModalProps> = ({
  open,
  onClose,
  sourceClaimId,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const getAccessHeader = useAuthenticatedFetch();

  // state hooks
  const [textFieldValue, setTextFieldValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [claimIdError, setClaimIdError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFeedback, setShowFeedback] = useState<FEEDBACK>(FEEDBACK.NONE);
  const [targetClaimId, setTargetClaimId] = useState("");

  const resetAndClose = () => {
    setTextFieldValue("");
    setIsLoading(false);
    setClaimIdError(false);
    setErrorMessage("");
    setTargetClaimId("");
    onClose();
  };

  const handleTextFieldChange = (event) => {
    setClaimIdError(false);
    setErrorMessage("");
    setTextFieldValue(event.target.value);
  };

  const trimmedTarget = textFieldValue.trim();
  const isSameClaimAsSource =
    trimmedTarget.length > 0 &&
    trimmedTarget.toLowerCase() === sourceClaimId.trim().toLowerCase();

  const handleSave = async () => {
    if (isSameClaimAsSource) {
      setClaimIdError(true);
      setErrorMessage(t("DASHBOARD.HEADER.MERGE_SAME_CLAIM_ERROR"));
      return;
    }
    try {
      setIsLoading(true);
      setTargetClaimId(textFieldValue);

      const response = await fetch(`/api/claims/${sourceClaimId}/merge`, {
        method: "PUT",
        headers: await getAccessHeader(),
        body: JSON.stringify({
          target_claim_id: textFieldValue,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setShowFeedback(FEEDBACK.SUCCESS);
        // Redirect to target claim after short delay
        setTimeout(() => {
          window.location.href = `/claims/${textFieldValue}`;
        }, 1000);
      } else {
        setIsLoading(false);
        setShowFeedback(FEEDBACK.ERROR);
        setClaimIdError(true);
        setErrorMessage(result.error || "Failed to merge claims");
      }
    } catch (error: any) {
      console.log("Error:", error.message);
      setIsLoading(false);
      setShowFeedback(FEEDBACK.ERROR);
      setClaimIdError(true);
      setErrorMessage("An unexpected error occurred");
    }
  };

  const handlePaste = async () => {
    const pastedText: string = await navigator.clipboard.readText();
    setTextFieldValue(pastedText.trim());
  };

  const handleFeedbackClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setShowFeedback(FEEDBACK.NONE);
  };

  return (
    <>
      <Modal open={open}>
        <Box sx={modalBoxStyle}>
          <Card sx={modalCardStyle}>
            <CardHeader
              sx={{ paddingBottom: "8px" }}
              title={
                <Typography variant="h5">
                  {t("DASHBOARD.HEADER.MERGE_CLAIM_MODAL_TITLE")}
                </Typography>
              }
              action={
                <IconButton onClick={() => resetAndClose()}>
                  <CloseIcon />
                </IconButton>
              }
            />

            <CardContent>
              <Typography variant="bodySm" sx={{ mb: 2 }}>
                {t("DASHBOARD.HEADER.MERGE_CLAIM_DESCRIPTION")}
              </Typography>

              {/* Input Field */}
              <TextField
                sx={{
                  mb: "10px",
                  "& .MuiInputBase-input": {
                    ...typography.titleMd,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottom: !textFieldValue ? "1px dashed" : "1px solid",
                    borderBottomColor:
                      mode === "light" ? neutral[200] : neutral[600],
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor:
                      mode === "light" ? neutral[200] : neutral[600],
                  },
                }}
                label={t("DASHBOARD.HEADER.TARGET_CLAIM_ID")}
                variant="standard"
                fullWidth
                value={textFieldValue}
                onChange={handleTextFieldChange}
                disabled={isLoading}
                helperText={
                  isSameClaimAsSource
                    ? t("DASHBOARD.HEADER.MERGE_SAME_CLAIM_ERROR")
                    : claimIdError
                      ? errorMessage
                      : ""
                }
                error={claimIdError || isSameClaimAsSource}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {isLoading ? (
                          <CircularProgress size={20} />
                        ) : claimIdError || isSameClaimAsSource ? (
                          <ErrorIcon />
                        ) : (
                          <Tooltip title={t("DASHBOARD.INPUT_PASTE")}>
                            <IconButton onClick={handlePaste} size="small">
                              <PasteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {/* CTA's */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 4,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{
                    width: "267px",
                    height: "48px",
                    color: mode === "light" ? neutral[700] : neutral[200],
                  }}
                  onClick={() => resetAndClose()}
                >
                  {t("GENERAL.CANCEL")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "267px",
                    height: "48px",
                  }}
                  onClick={handleSave}
                  disabled={
                    !trimmedTarget || isLoading || isSameClaimAsSource
                  }
                >
                  {t("DASHBOARD.HEADER.MERGE_CONFIRM")}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Modal>

      {/* Custom Feedback */}
      {showFeedback !== FEEDBACK.NONE && (
        <Snackbar
          open={true}
          autoHideDuration={
            showFeedback === FEEDBACK.SUCCESS ? 10000 : AUTO_HIDE_DURATION_ERROR
          }
          onClose={handleFeedbackClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            variant="filled"
            severity={showFeedback === FEEDBACK.SUCCESS ? "success" : "error"}
            icon={
              showFeedback === FEEDBACK.SUCCESS ? (
                <CheckCircleIcon />
              ) : (
                <ErrorIcon />
              )
            }
            sx={{ color: common["white"] }}
            action={
              <IconButton sx={{ pt: "5px" }} onClick={handleFeedbackClose}>
                <CloseIconWhite />
              </IconButton>
            }
          >
            {showFeedback === FEEDBACK.ERROR
              ? t("GENERAL.ERROR")
              : t("DASHBOARD.HEADER.MERGE_SUCCESS")}

            {showFeedback === FEEDBACK.SUCCESS && targetClaimId && (
              <Typography variant="bodySm" sx={{ display: "block", marginTop: "5px" }}>
                {t("DASHBOARD.HEADER.REDIRECTING_TO_TARGET")}
              </Typography>
            )}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default MergeModal;
